import clsx from 'clsx';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { ShadowRoot } from '@/src/components/common/Elements/ShadowRoot/ShadowRoot';
import { useMarkRead } from '@/src/components/project/Inbox2/components/EntryPanel/EmailThreadEntryViewer/api/markReadEmail';
import ActiveFilesBadge from '@/src/components/project/Inbox2/components/EntryPanel/EmailThreadEntryViewer/components/Attachments/ActiveFilesBadge/ActiveFilesBadge';
import CreateReply from '@/src/components/project/Inbox2/components/EntryPanel/EmailThreadEntryViewer/components/EmailThreadViewer/CreateReply/CreateReply';
import Attachments from '@/src/components/project/Inbox2/components/EntryPanel/EmailThreadEntryViewer/components/EmailThreadViewer/EmailEntries/EmailEntryExpanded/Attachments/Attachments';
import {
  getEmailEntryToUpdateStatus,
  getEmailTypeDisplay,
  getUserDisplay,
} from '@/src/components/project/Inbox2/components/EntryPanel/EmailThreadEntryViewer/components/EmailThreadViewer/EmailEntries/EmailEntryExpanded/EmailEntryExpanded';
import { checkOverflow } from '@/src/components/project/Inbox2/components/EntryPanel/EmailThreadEntryViewer/components/EmailThreadViewer/EmailEntries/EmailEntryExpanded/EmailEntryExpandedDetails';
import ToggleCreateReply from '@/src/components/project/Inbox2/components/EntryPanel/EmailThreadEntryViewer/components/EmailThreadViewer/EmailEntries/EmailEntryExpanded/ToggleCreateReply/ToggleCreateReply';
import RecipientListPopper from '@/src/components/project/Inbox2/components/EntryPanel/EmailThreadEntryViewer/components/EmailThreadViewer/EmailEntries/RecipientListPopper/RecipientListPopper';
import UpdateEmailStatus from '@/src/components/project/Inbox2/components/EntryPanel/EmailThreadEntryViewer/components/UpdateEmailStatus/UpdateEmailStatus';
import { useEmailThreadEntryViewer } from '@/src/components/project/Inbox2/components/EntryPanel/EmailThreadEntryViewer/store/emailThreadEntryViewer.store';
import { MESSAGE_HISTORY_TW_SUPPORT_STYLE } from '@/src/components/project/Inbox2/Config/inbox.config';
import AttachmentsCount from '@/src/components/project/Inbox2/Elements/Buttons/AttachmentsCount/AttachmentsCount';
import { EllipsisThreadButton } from '@/src/components/project/Inbox2/Elements/Buttons/EllipsisThreadButton/EllipsisThreadButton';
import EntryCard from '@/src/components/project/Inbox2/Elements/EntryCard/EntryCard';
import { parseInlineImages } from '@/src/components/project/Inbox2/Helper';
import {
  EmailEntry,
  EmailEntryFiles,
  EmailEntryType,
  EmailFileEntry,
} from '@/src/components/project/Inbox2/types';
import { DEFAULT_DATE_FORMAT } from '@/src/config';
import { useToggle } from '@/src/hooks/useToggle';
import { Authorization } from '@/src/lib/authorization/authorization';
import { RootState } from '@/src/reducers/types';
import { parseAndSanitizeHtml } from '@/src/util/html';

const DATE_FORMAT = `${DEFAULT_DATE_FORMAT} h:m A`;

type EmailEntryExpandedProps = {
  emailEntry: EmailEntry;
  onSetEmailFileEntry: (emailEntryFile: EmailFileEntry) => void;
};

const EmailEntry = ({
  emailEntry,
  onSetEmailFileEntry,
}: EmailEntryExpandedProps) => {
  const searchTerm = useSelector((state: RootState) => state.filters.searchTerm);
  const { projectId } = useParams<{ projectId: string }>();
  const { replyType, emailEntryToReply } = useEmailThreadEntryViewer();

  const markReadMutation = useMarkRead({
    projectId,
    inboxEntryId: emailEntry.inboxEntryId,
    emailId: emailEntry.id,
  });

  const emailContentRef = useRef(null);
  const [height, setHeight] = useState(null);
  const [showEllipsisContent, toggleShowEllipsisContent] = useToggle(false);

  useEffect(() => {
    if (!emailContentRef.current) {
      return;
    }

    setHeight(emailContentRef.current.offsetHeight);
  }, [emailEntry.html]);

  // Email opened listener
  useEffect(() => {
    if (!emailEntry.isRead) {
      markReadMutation.mutateAsync({
        emailId: emailEntry.id,
        inboxEntryId: emailEntry.inboxEntryId,
        projectId,
        read: true,
      });
    }
  }, []);

  const dateReceived = moment(emailEntry.dateAdded).format(DATE_FORMAT);
  const dateReceivedFromNow = moment(emailEntry.dateAdded).fromNow();
  const isOverflow = checkOverflow(height);
  const parsedMessage = parseAndSanitizeHtml(
    parseInlineImages(emailEntry.html),
    { FORBID_TAGS: [] },
    searchTerm
  );
  const userDisplay = getUserDisplay(emailEntry.from);
  const emailEntryToUpdateStatus = getEmailEntryToUpdateStatus(emailEntry);
  const emailTypeDisplay = getEmailTypeDisplay(emailEntry.emailType);
  const isSentEmailType = emailEntry.emailType === EmailEntryType.SENT;

  return (
    <React.Fragment>
      <EntryCard
        className={clsx(
          'contaienr--emailExpanded',
          'tw-rounded-[5px]',
          'tw-p-[10px] tw-mb-[10px] tw-mx-[2px]',
          emailEntry.isRead && 'tw-bg-cfo-grey-100'
        )}
        isActive={true}
      >
        {/* Email Header ( Avatar, Name, Date Received) */}
        <div>
          <div
            className={clsx(
              'tw-flex tw-items-center tw-justify-between',
              'tw-pb-[10px]',
              'tw-cursor-pointer'
            )}
          >
            <div className={clsx('tw-flex tw-items-center')}>{userDisplay}</div>

            <div className={clsx('tw-flex tw-items-center')}>
              <p className={clsx('tw-p-0', 'tw-text-cfo-black')}>
                {emailTypeDisplay} {dateReceived} ({dateReceivedFromNow})
              </p>
            </div>
          </div>

          <div className="tw-cursor-default">
            {/* Email Status */}
            <div className={clsx('tw-flex tw-items-center tw-justify-between', 'tw-ml-[40px]')}>
              <RecipientListPopper
                emailEntry={{
                  from: emailEntry.from,
                  to: emailEntry.to,
                  cc: emailEntry.cc,
                }}
              />

              {!isSentEmailType && (
                <div className={'tw-flex tw-items-center'}>
                  <UpdateEmailStatus emailEntry={emailEntryToUpdateStatus} />
                </div>
              )}
            </div>

            {/* Email Summary */}
            <div
              ref={emailContentRef}
              className={clsx(
                'container--emailBody',
                'tw-pl-[40px] tw-py-[15px]',
                'tw-break-words'
              )}
            >
              <div
                className={clsx(
                  'container--parsedMessage',
                  'tw-text-cfo-black',
                  'tw-m-0',
                  showEllipsisContent ? 'tw-max-h-full' : 'tw-max-h-[800px] tw-overflow-hidden'
                )}
              >
                <ShadowRoot
                  htmlView={parsedMessage}
                  styleTagContent={MESSAGE_HISTORY_TW_SUPPORT_STYLE}
                />
              </div>

              {isOverflow && (
                <EllipsisThreadButton
                  className={clsx('tw-border-solid tw-border-[1px] tw-border-cfo-grey-500')}
                  onClick={toggleShowEllipsisContent}
                />
              )}
            </div>

            {/* Email Attachments Count */}
            <div
              className={clsx(
                'container--emailAttachments',
                'tw-w-full',
                'tw-border-solid tw-border-t-[1px] tw-border-t-cfo-grey-500',
                'tw-p-[10px] tw-mt-[10px]',
                emailEntry.files.list.length <= 0 && 'tw-hidden'
              )}
            >
              <div className={clsx('tw-w-full', 'tw-flex tw-items-center')}>
                {emailEntry.files.list.length > 0 && (
                  <AttachmentsCount
                    count={emailEntry.files.total}
                    className={clsx(
                      'tw-px-[10px] tw-py-[5px]',
                      'tw-rounded-[34px] ',
                      'tw-bg-cfo-grey-100'
                    )}
                  />
                )}
                {!isSentEmailType && <ActiveFilesBadge count={emailEntry.files.total} />}
              </div>

              {emailEntry.files.list.length > 0 && (
                <Attachments
                  className={clsx('tw-mt-[10px]')}
                  inboxEntryId={emailEntry.inboxEntryId}
                  preloadedEmailFileEntries={emailEntry.files as EmailEntryFiles}
                  onSetEmailFileEntry={onSetEmailFileEntry}
                  isReadonly={true}
                  isSentEmailType={isSentEmailType}
                />
              )}
            </div>
          </div>
        </div>
        <Authorization policyCheck={emailEntry.permissions.createReply}>
          <ToggleCreateReply emailEntry={emailEntry} emailEntryListItem={null} />
        </Authorization>
      </EntryCard>
      {replyType && emailEntryToReply && (
        <Authorization policyCheck={emailEntryToReply.permissions.createReply}>
          <CreateReply
            key={`cr_${emailEntryToReply.id}`}
            projectId={projectId}
            emailEntry={emailEntryToReply}
          />
        </Authorization>
      )}
    </React.Fragment>
  );
};

export default EmailEntry;
